/** Role permission keys used by nav / route gating. */
export const ROLE_PERMS = {
  conversations: ["viewAllChats", "viewTeamChats"],
  contacts: ["viewAllChats", "viewTeamChats"],
  orders: ["viewOrderFollowup", "editOrderFollowup"],
  campaign: ["broadcastCampaign", "campaignAnalytics"],
  templateMessage: ["broadcastCampaign", "campaignAnalytics"],
  chatAutomations: ["viewChatbot", "editChatbot"],
  messageAutomations: ["viewRecurring", "editRecurring"],
  eventDrivenAutomations: [
    "viewEventDrivenSequence",
    "editEventDrivenSequence",
  ],
  calendar: ["viewAppointment", "editAppointment"],
  appointmentSettings: ["viewAppointment", "editAppointment"],
  userManagement: ["viewUser", "editUser"],
};

function getUserPayload(authOrUser) {
  if (!authOrUser) return null;
  // Auth context shape: auth.user.user, or login payload / userDetails
  if (authOrUser.user?.user) return authOrUser.user.user;
  if (authOrUser.user && (authOrUser.user.role_permissions != null || authOrUser.user.permission != null || authOrUser.user.is_admin != null)) {
    return authOrUser.user;
  }
  return authOrUser;
}

export function isCompanyAdmin(authOrUser) {
  return !!getUserPayload(authOrUser)?.is_admin;
}

/**
 * Check whether the user has any of the given role permission keys.
 * - Company admins always pass.
 * - Sessions without role_permissions (pre-feature login) keep previous behavior (allow).
 * - Empty keys means admin-only.
 */
export function hasRolePermission(authOrUser, keys = []) {
  const user = getUserPayload(authOrUser);
  if (!user) return false;
  if (user.is_admin) return true;

  const perms = user.role_permissions;
  // Legacy session before role_permissions were added to login
  if (perms == null) return true;
  if (!keys?.length) return false;
  return keys.some((key) => perms.includes(key));
}

/** Company feature flags (`user.permission`), matching existing Orders/Automation behavior. */
export function hasCompanyFeature(authOrUser, featureKey) {
  const user = getUserPayload(authOrUser);
  const companyPerms = user?.permission;
  if (featureKey === "integrations") {
    return !!companyPerms?.integrations;
  }
  return companyPerms?.[featureKey] !== false;
}

export function canAccessOrders(authOrUser) {
  return (
    hasCompanyFeature(authOrUser, "orders") &&
    hasRolePermission(authOrUser, ROLE_PERMS.orders)
  );
}

export function canAccessConversations(authOrUser) {
  return hasRolePermission(authOrUser, ROLE_PERMS.conversations);
}

export function canAccessContacts(authOrUser) {
  return hasRolePermission(authOrUser, ROLE_PERMS.contacts);
}

export function canAccessCampaign(authOrUser) {
  return hasRolePermission(authOrUser, ROLE_PERMS.campaign);
}

export function canAccessTemplateMessage(authOrUser) {
  return hasRolePermission(authOrUser, ROLE_PERMS.templateMessage);
}

export function canAccessAutomations(authOrUser) {
  if (!hasCompanyFeature(authOrUser, "automations")) return false;
  return (
    hasRolePermission(authOrUser, ROLE_PERMS.chatAutomations) ||
    hasRolePermission(authOrUser, ROLE_PERMS.messageAutomations) ||
    hasRolePermission(authOrUser, ROLE_PERMS.eventDrivenAutomations)
  );
}

export function canAccessChatAutomations(authOrUser) {
  return (
    hasCompanyFeature(authOrUser, "automations") &&
    hasRolePermission(authOrUser, ROLE_PERMS.chatAutomations)
  );
}

export function canAccessMessageAutomations(authOrUser) {
  return (
    hasCompanyFeature(authOrUser, "automations") &&
    hasRolePermission(authOrUser, ROLE_PERMS.messageAutomations)
  );
}

export function canAccessEventDrivenAutomations(authOrUser) {
  return (
    hasCompanyFeature(authOrUser, "automations") &&
    hasRolePermission(authOrUser, ROLE_PERMS.eventDrivenAutomations)
  );
}

export function canAccessCalendar(authOrUser) {
  return hasRolePermission(authOrUser, ROLE_PERMS.calendar);
}

export function canAccessAppointmentSettings(authOrUser) {
  return hasRolePermission(authOrUser, ROLE_PERMS.appointmentSettings);
}

export function canAccessUserManagement(authOrUser) {
  return hasRolePermission(authOrUser, ROLE_PERMS.userManagement);
}

/** Settings with no role key (company config) — admins only once roles are enforced. */
export function canAccessAdminSettings(authOrUser) {
  return hasRolePermission(authOrUser, []);
}

export function canAccessIntegrations(authOrUser) {
  return (
    hasCompanyFeature(authOrUser, "integrations") &&
    hasRolePermission(authOrUser, [])
  );
}

export function canAccessSettingsMenu(authOrUser) {
  return (
    canAccessAppointmentSettings(authOrUser) ||
    canAccessAdminSettings(authOrUser) ||
    canAccessUserManagement(authOrUser)
  );
}

/** First route the user is allowed to open after login. */
export function getDefaultAuthenticatedRoute(authOrUser) {
  if (canAccessConversations(authOrUser)) return "/conversations";
  if (canAccessOrders(authOrUser)) return "/orders";
  if (canAccessContacts(authOrUser)) return "/contacts";
  if (canAccessCampaign(authOrUser)) return "/Campaign";
  if (canAccessTemplateMessage(authOrUser)) return "/templateMessage";
  if (canAccessChatAutomations(authOrUser)) return "/chat_automations";
  if (canAccessMessageAutomations(authOrUser)) return "/message_automations";
  if (canAccessEventDrivenAutomations(authOrUser)) {
    return "/event_driven_automation";
  }
  if (canAccessCalendar(authOrUser)) return "/calender";
  if (canAccessUserManagement(authOrUser)) return "/UserManagement";
  return "/profile";
}
